import { Typography, Space, Tag, Divider, Button } from "antd";
import { CSSProperties, ReactNode } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { colors } from "config/theme";

const { Title, Text } = Typography;

interface ShowPageHeaderProps {
  title?: string;
  subtitle?: string;
  status?: "loading" | "live" | "error";
  statusLabel?: string;
  onBack?: () => void;
  extra?: ReactNode;
}

const headerStyles = {
  container: {
    marginBottom: 24,
  } as CSSProperties,
  topRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap" as const,
  } as CSSProperties,
  leftSection: {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
  } as CSSProperties,
  backButton: {
    marginTop: 4,
  } as CSSProperties,
  titleGroup: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 4,
  } as CSSProperties,
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  } as CSSProperties,
  title: {
    fontSize: 24,
    fontWeight: 600,
    color: colors.textPrimary,
    margin: 0,
    lineHeight: 1.3,
  } as CSSProperties,
  subtitle: {
    fontSize: 14,
    color: colors.textTertiary,
    margin: 0,
  } as CSSProperties,
  divider: {
    margin: "24px 0",
    borderColor: colors.borderLight,
  } as CSSProperties,
};

const statusConfig: Record<string, { color: string; label: string }> = {
  loading: { color: "orange", label: "Loading" },
  live: { color: "green", label: "Live" },
  error: { color: "red", label: "Error" },
};

export const ShowPageHeader: React.FC<ShowPageHeaderProps> = ({
  title,
  subtitle,
  status,
  statusLabel,
  onBack,
  extra,
}) => {
  const statusInfo = status ? statusConfig[status] : null;

  return (
    <div style={headerStyles.container}>
      <div style={headerStyles.topRow}>
        <div style={headerStyles.leftSection}>
          {onBack && (
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              style={headerStyles.backButton}
            />
          )}
          <div style={headerStyles.titleGroup}>
            <div style={headerStyles.titleRow}>
              {title && (
                <Title level={2} style={headerStyles.title}>
                  {title}
                </Title>
              )}
              {statusInfo && (
                <Tag color={statusInfo.color}>
                  {statusLabel || statusInfo.label}
                </Tag>
              )}
            </div>
            {subtitle && (
              <Text style={headerStyles.subtitle}>{subtitle}</Text>
            )}
          </div>
        </div>
        {extra && <Space wrap>{extra}</Space>}
      </div>
      <Divider style={headerStyles.divider} />
    </div>
  );
};

export default ShowPageHeader;
